import React, { useEffect, useState, useCallback, useRef } from 'react';
import { weeklyDollarApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';
import { 
  Plus, Save, Trash2, RefreshCw, Printer, FileSpreadsheet, FileText,
  X, Check, Edit2, Download, Upload, CheckCircle, Lock, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

// Decimal-safe calculation helpers (mirror backend)
const toNum = (v) => parseFloat(v) || 0;
const round2 = (v) => Math.round(v * 100) / 100;

const calculateClientTotal = (mcxBal, foBal, mcxBrk, foBrk) => {
  return round2(toNum(mcxBal) + toNum(foBal) - toNum(mcxBrk) - toNum(foBrk));
};

const calculateClientNetBal = (clientTotal, clientPercent) => {
  return round2(toNum(clientTotal) * (toNum(clientPercent) / 100));
};

const calculateFinalBal = (clientTotal, parentPercent, brkRemMcx, brkRemFo) => {
  return round2((toNum(clientTotal) * (toNum(parentPercent) / 100)) + toNum(brkRemMcx) + toNum(brkRemFo));
};

const recalculateRow = (row) => {
  const clientTotal = calculateClientTotal(row.mcx_bal, row.fo_bal, row.mcx_brk, row.fo_brk);
  const clientNetBal = calculateClientNetBal(clientTotal, row.client_percent);
  const finalBal = calculateFinalBal(clientTotal, row.parent_percent, row.brk_rem_mcx, row.brk_rem_fo);
  return { ...row, client_total: clientTotal, client_net_bal: clientNetBal, final_bal: finalBal };
};

const emptyRow = () => ({
  _isNew: true,
  _isEditing: true,
  entry_id: `new_${Date.now()}`,
  client_id: '',
  client_name: '',
  client_name_snapshot: '',
  code_or_id: '',
  exchange: '',
  mcx_bal: 0,
  fo_bal: 0,
  mcx_brk: 0,
  fo_brk: 0,
  client_percent: 0,
  client_total: 0,
  client_net_bal: 0,
  parent_client_id: '',
  parent_name: '',
  parent_name_snapshot: '',
  parent_percent: 0,
  brk_rem_mcx: 0,
  brk_rem_fo: 0,
  final_bal: 0,
  tallied: false,
  settlement_status: 'PENDING',
  remarks: ''
});

export function WeeklyDollarPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [clients, setClients] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [importDialog, setImportDialog] = useState({ open: false, preview: null, loading: false });
  const tableRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, templateRes] = await Promise.all([
        weeklyDollarApi.list({ entry_date: entryDate }),
        weeklyDollarApi.getTemplateData()
      ]);
      setEntries(listRes.entries || []);
      setTotals(listRes.totals || {});
      setClients(templateRes.clients || []);
      setExchanges(templateRes.exchanges || []);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [entryDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Add new row
  const handleAddRow = () => {
    const newRow = emptyRow();
    setEntries(prev => [...prev, newRow]);
    setEditingRowId(newRow.entry_id);
    // Scroll to bottom
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollTop = tableRef.current.scrollHeight;
      }
    }, 100);
  };

  // Update row field
  const handleFieldChange = (entryId, field, value) => {
    setEntries(prev => prev.map(row => {
      if (row.entry_id !== entryId) return row;
      const updated = { ...row, [field]: value };
      // Recalculate on numeric field change
      if (['mcx_bal', 'fo_bal', 'mcx_brk', 'fo_brk', 'client_percent', 'parent_percent', 'brk_rem_mcx', 'brk_rem_fo'].includes(field)) {
        return recalculateRow(updated);
      }
      return updated;
    }));
  };

  // Client selection handler
  const handleClientSelect = (entryId, clientId, isParent = false) => {
    const client = clients.find(c => c.client_id === clientId);
    if (!client) return;
    
    setEntries(prev => prev.map(row => {
      if (row.entry_id !== entryId) return row;
      if (isParent) {
        return { ...row, parent_client_id: clientId, parent_name: client.name };
      }
      return { 
        ...row, 
        client_id: clientId, 
        client_name: client.name,
        exchange: client.exchange || row.exchange
      };
    }));
  };

  // Save row
  const handleSaveRow = async (row) => {
    if (!row.client_id || !row.exchange) {
      toast.error('Client and Exchange are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client_id: row.client_id,
        client_name: row.client_name,
        code_or_id: row.code_or_id,
        exchange: row.exchange,
        mcx_bal: toNum(row.mcx_bal),
        fo_bal: toNum(row.fo_bal),
        mcx_brk: toNum(row.mcx_brk),
        fo_brk: toNum(row.fo_brk),
        client_percent: toNum(row.client_percent),
        parent_client_id: row.parent_client_id || null,
        parent_name: row.parent_name || null,
        parent_percent: toNum(row.parent_percent),
        brk_rem_mcx: toNum(row.brk_rem_mcx),
        brk_rem_fo: toNum(row.brk_rem_fo),
        entry_date: entryDate,
        remarks: row.remarks
      };

      if (row._isNew) {
        await weeklyDollarApi.create(payload);
        toast.success('Entry created');
      } else {
        await weeklyDollarApi.update(row.entry_id, payload);
        toast.success('Entry updated');
      }

      setEditingRowId(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Delete row
  const handleDeleteRow = async (row) => {
    if (row._isNew) {
      setEntries(prev => prev.filter(r => r.entry_id !== row.entry_id));
      return;
    }

    if (!window.confirm('Delete this entry?')) return;

    try {
      await weeklyDollarApi.delete(row.entry_id);
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Delete failed');
    }
  };

  // Cancel edit
  const handleCancelEdit = (row) => {
    if (row._isNew) {
      setEntries(prev => prev.filter(r => r.entry_id !== row.entry_id));
    }
    setEditingRowId(null);
    fetchData();
  };

  // Export handlers
  const handleExportXlsx = () => {
    window.open(weeklyDollarApi.exportXlsx(entryDate), '_blank');
  };

  const handleExportPdf = () => {
    window.open(weeklyDollarApi.exportPdf(entryDate), '_blank');
  };

  const handlePrint = () => {
    window.print();
  };

  // ============ NEW ACTION HANDLERS ============

  // Toggle tallied status
  const handleToggleTallied = async (row) => {
    if (row.settlement_status === 'SETTLED') {
      toast.error('Cannot modify settled entry');
      return;
    }
    try {
      await weeklyDollarApi.toggleTallied(row.entry_id, !row.tallied);
      toast.success(row.tallied ? 'Untallied' : 'Marked as tallied');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to toggle tallied');
    }
  };

  // Settle single entry
  const handleSettle = async (row) => {
    if (row.settlement_status === 'SETTLED') {
      toast.error('Entry already settled');
      return;
    }
    if (row.final_bal === 0) {
      toast.error('Cannot settle entry with zero balance');
      return;
    }
    if (!window.confirm(`Settle this entry?\nFinal Balance: ${formatNum(row.final_bal)}`)) return;
    
    try {
      await weeklyDollarApi.settle(row.entry_id);
      toast.success('Entry settled successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Settlement failed');
    }
  };

  // Settle selected entries
  const handleSettleSelected = async () => {
    const selectedEntries = entries.filter(e => selectedRows.has(e.entry_id) && e.settlement_status !== 'SETTLED');
    if (selectedEntries.length === 0) {
      toast.error('No pending entries selected');
      return;
    }
    if (!window.confirm(`Settle ${selectedEntries.length} selected entries?`)) return;
    
    try {
      const result = await weeklyDollarApi.settleBulk(selectedEntries.map(e => e.entry_id));
      toast.success(`Settled ${result.settled} entries`);
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} entries had errors`);
      }
      setSelectedRows(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Bulk settlement failed');
    }
  };

  // Toggle row selection
  const handleToggleRowSelect = (entryId) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  // Select all / none
  const handleSelectAll = () => {
    if (selectedRows.size === entries.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(entries.map(e => e.entry_id)));
    }
  };

  // Upload Excel handler
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportDialog({ open: true, preview: null, loading: true });
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const result = await weeklyDollarApi.importPreview(formData);
      setImportDialog({ open: true, preview: result, loading: false });
    } catch (error) {
      toast.error(error.message || 'Failed to parse Excel file');
      setImportDialog({ open: false, preview: null, loading: false });
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Confirm import
  const handleConfirmImport = async () => {
    if (!importDialog.preview) return;
    
    const validRows = importDialog.preview.rows.filter(r => r.is_valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }
    
    setImportDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const result = await weeklyDollarApi.importConfirm(validRows, entryDate);
      toast.success(`Imported ${result.created} entries`);
      setImportDialog({ open: false, preview: null, loading: false });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Import failed');
      setImportDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Format number for display
  const formatNum = (v, colored = false) => {
    const num = toNum(v);
    const formatted = num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (!colored) return formatted;
    return (
      <span className={cn(num > 0 && 'text-emerald-500', num < 0 && 'text-red-500')}>
        {formatted}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col" data-testid="weekly-dollar-page">
      {/* Hidden file input for Excel upload */}
      <input 
        ref={fileInputRef}
        type="file" 
        accept=".xlsx,.xls" 
        className="hidden" 
        onChange={handleFileSelect}
      />
      
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-card border-b print:hidden">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-primary">Weekly$</span>
          <Input
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            className="h-8 w-36"
          />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" onClick={handleAddRow} data-testid="add-row-btn">
            <Plus className="h-4 w-4 mr-1" />Add Row
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-excel-btn"
          >
            <Upload className="h-4 w-4 mr-1" />Upload Excel
          </Button>
          {selectedRows.size > 0 && (
            <>
              <Button 
                size="sm" 
                variant="default"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSettleSelected}
                data-testid="settle-selected-btn"
              >
                <CheckCircle className="h-4 w-4 mr-1" />Settle ({selectedRows.size})
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={handleExportXlsx}>
            <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPdf}>
            <FileText className="h-4 w-4 mr-1" />PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Spreadsheet Grid */}
      <div 
        ref={tableRef}
        className="flex-1 overflow-auto bg-background"
        style={{ maxHeight: 'calc(100vh - 180px)' }}
      >
        <table className="w-full text-xs border-collapse min-w-[1600px]">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="px-1 py-1.5 text-center border font-semibold w-8">
                <Checkbox 
                  checked={selectedRows.size === entries.length && entries.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </th>
              <th className="px-1 py-1.5 text-left border font-semibold w-8">#</th>
              <th className="px-1 py-1.5 text-center border font-semibold w-16">Status</th>
              <th className="px-1 py-1.5 text-left border font-semibold min-w-[140px]">CLIENT NAME</th>
              <th className="px-1 py-1.5 text-left border font-semibold w-20">ID</th>
              <th className="px-1 py-1.5 text-left border font-semibold w-20">EXCHANGE</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-24">MCX BAL</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-24">FO BAL</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-20">MCX BRK</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-20">FO BRK</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-16">CLIENT@</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-28 bg-amber-50 dark:bg-amber-950">CLIENT NET BAL</th>
              <th className="px-1 py-1.5 text-left border font-semibold min-w-[120px]">PARENT</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-16">Parent@</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-24">BRK REM Mcx</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-24">BRK REM F&O</th>
              <th className="px-1 py-1.5 text-right border font-semibold w-28 bg-primary/10">FINAL BAL</th>
              <th className="px-1 py-1.5 text-center border font-semibold w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((row, idx) => {
              const isEditing = editingRowId === row.entry_id || row._isNew;
              const isSettled = row.settlement_status === 'SETTLED';
              const isTallied = row.tallied;
              const clientName = row.client_name_snapshot || row.client_name;
              const parentName = row.parent_name_snapshot || row.parent_name;
              
              return (
                <tr 
                  key={row.entry_id} 
                  className={cn(
                    "border-b hover:bg-muted/30",
                    isEditing && "bg-primary/5",
                    isTallied && !isSettled && "bg-amber-50/50 dark:bg-amber-950/20",
                    isSettled && "bg-emerald-50/30 dark:bg-emerald-950/10 opacity-70"
                  )}
                >
                  {/* Checkbox */}
                  <td className="px-1 py-0.5 border text-center">
                    <Checkbox 
                      checked={selectedRows.has(row.entry_id)}
                      onCheckedChange={() => handleToggleRowSelect(row.entry_id)}
                      disabled={isSettled}
                    />
                  </td>
                  <td className="px-1 py-0.5 border text-center text-muted-foreground">{idx + 1}</td>
                  
                  {/* Status */}
                  <td className="px-1 py-0.5 border text-center">
                    {isSettled ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                        <Lock className="h-3 w-3" />SETTLED
                      </span>
                    ) : isTallied ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                        <Check className="h-3 w-3" />TALLIED
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">PENDING</span>
                    )}
                  </td>
                  
                  {/* CLIENT NAME */}
                  <td className="px-1 py-0.5 border">
                    {isEditing && !isSettled ? (
                      <Select 
                        value={row.client_id} 
                        onValueChange={(v) => handleClientSelect(row.entry_id, v)}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(c => (
                            <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="font-medium">{clientName}</span>
                    )}
                  </td>
                  
                  {/* ID */}
                  <td className="px-1 py-0.5 border">
                    {isEditing ? (
                      <Input
                        value={row.code_or_id || ''}
                        onChange={(e) => handleFieldChange(row.entry_id, 'code_or_id', e.target.value)}
                        className="h-7 text-xs px-1"
                      />
                    ) : (
                      <span className="text-amber-600">{row.code_or_id}</span>
                    )}
                  </td>
                  
                  {/* EXCHANGE */}
                  <td className="px-1 py-0.5 border">
                    {isEditing ? (
                      <Select 
                        value={row.exchange} 
                        onValueChange={(v) => handleFieldChange(row.entry_id, 'exchange', v)}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue placeholder="Exch" />
                        </SelectTrigger>
                        <SelectContent>
                          {exchanges.map(e => (
                            <SelectItem key={e} value={e}>{e}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      row.exchange
                    )}
                  </td>
                  
                  {/* MCX BAL */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={row.mcx_bal}
                        onChange={(e) => handleFieldChange(row.entry_id, 'mcx_bal', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : formatNum(row.mcx_bal, true)}
                  </td>
                  
                  {/* FO BAL */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={row.fo_bal}
                        onChange={(e) => handleFieldChange(row.entry_id, 'fo_bal', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : formatNum(row.fo_bal, true)}
                  </td>
                  
                  {/* MCX BRK */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={row.mcx_brk}
                        onChange={(e) => handleFieldChange(row.entry_id, 'mcx_brk', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : formatNum(row.mcx_brk)}
                  </td>
                  
                  {/* FO BRK */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={row.fo_brk}
                        onChange={(e) => handleFieldChange(row.entry_id, 'fo_brk', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : formatNum(row.fo_brk)}
                  </td>
                  
                  {/* CLIENT@ */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.1"
                        value={row.client_percent}
                        onChange={(e) => handleFieldChange(row.entry_id, 'client_percent', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : `${toNum(row.client_percent).toFixed(1)}%`}
                  </td>
                  
                  {/* CLIENT NET BAL (calculated) */}
                  <td className="px-1 py-0.5 border text-right font-mono font-semibold bg-amber-50/50 dark:bg-amber-950/30">
                    {formatNum(row.client_net_bal, true)}
                  </td>
                  
                  {/* PARENT */}
                  <td className="px-1 py-0.5 border">
                    {isEditing ? (
                      <Select 
                        value={row.parent_client_id || 'none'} 
                        onValueChange={(v) => handleClientSelect(row.entry_id, v === 'none' ? '' : v, true)}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue placeholder="Parent" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {clients.map(c => (
                            <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-blue-600">{row.parent_name || '-'}</span>
                    )}
                  </td>
                  
                  {/* Parent@ */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.1"
                        value={row.parent_percent}
                        onChange={(e) => handleFieldChange(row.entry_id, 'parent_percent', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : `${toNum(row.parent_percent).toFixed(1)}%`}
                  </td>
                  
                  {/* BRK REM Mcx */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={row.brk_rem_mcx}
                        onChange={(e) => handleFieldChange(row.entry_id, 'brk_rem_mcx', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : formatNum(row.brk_rem_mcx)}
                  </td>
                  
                  {/* BRK REM F&O */}
                  <td className="px-1 py-0.5 border text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={row.brk_rem_fo}
                        onChange={(e) => handleFieldChange(row.entry_id, 'brk_rem_fo', e.target.value)}
                        className="h-7 text-xs px-1 text-right"
                      />
                    ) : formatNum(row.brk_rem_fo)}
                  </td>
                  
                  {/* FINAL BAL (calculated) */}
                  <td className="px-1 py-0.5 border text-right font-mono font-bold bg-primary/5">
                    {formatNum(row.final_bal, true)}
                  </td>
                  
                  {/* Actions */}
                  <td className="px-1 py-0.5 border text-center">
                    {isEditing ? (
                      <div className="flex justify-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-emerald-600" 
                          onClick={() => handleSaveRow(row)}
                          disabled={saving}
                          title="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-muted-foreground" 
                          onClick={() => handleCancelEdit(row)}
                          title="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : isSettled ? (
                      <span className="text-[10px] text-muted-foreground">Locked</span>
                    ) : (
                      <div className="flex justify-center gap-0.5">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className={cn("h-6 w-6", isTallied && "text-amber-600")}
                          onClick={() => handleToggleTallied(row)}
                          title={isTallied ? "Untally" : "Mark Tallied"}
                        >
                          <CheckCircle className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-emerald-600" 
                          onClick={() => handleSettle(row)}
                          title="Settle"
                        >
                          <Lock className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => setEditingRowId(row.entry_id)}
                          title="Edit"
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-destructive" 
                          onClick={() => handleDeleteRow(row)}
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            
            {entries.length === 0 && !loading && (
              <tr>
                <td colSpan={18} className="px-4 py-8 text-center text-muted-foreground">
                  No entries found. Click "Add Row" to create one.
                </td>
              </tr>
            )}
          </tbody>
          
          {/* Footer Totals */}
          {entries.length > 0 && (
            <tfoot className="bg-muted/70 font-semibold sticky bottom-0">
              <tr>
                <td className="px-1 py-1.5 border" colSpan={6}>TOTAL ({entries.length} rows)</td>
                <td className="px-1 py-1.5 border text-right font-mono">{formatNum(totals.mcx_bal, true)}</td>
                <td className="px-1 py-1.5 border text-right font-mono">{formatNum(totals.fo_bal, true)}</td>
                <td className="px-1 py-1.5 border text-right font-mono">{formatNum(totals.mcx_brk)}</td>
                <td className="px-1 py-1.5 border text-right font-mono">{formatNum(totals.fo_brk)}</td>
                <td className="px-1 py-1.5 border"></td>
                <td className="px-1 py-1.5 border text-right font-mono bg-amber-50/50 dark:bg-amber-950/30">{formatNum(totals.client_net_bal, true)}</td>
                <td className="px-1 py-1.5 border" colSpan={2}></td>
                <td className="px-1 py-1.5 border text-right font-mono">{formatNum(totals.brk_rem_mcx)}</td>
                <td className="px-1 py-1.5 border text-right font-mono">{formatNum(totals.brk_rem_fo)}</td>
                <td className="px-1 py-1.5 border text-right font-mono bg-primary/5">{formatNum(totals.final_bal, true)}</td>
                <td className="px-1 py-1.5 border"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Mobile hint */}
      <div className="p-2 text-center text-xs text-muted-foreground bg-muted border-t md:hidden print:hidden">
        ← Scroll horizontally to view all columns →
      </div>

      {/* Import Preview Dialog */}
      <Dialog open={importDialog.open} onOpenChange={(open) => !open && setImportDialog({ open: false, preview: null, loading: false })}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Import Preview</DialogTitle>
            <DialogDescription>
              {importDialog.preview && (
                <span>
                  Total: {importDialog.preview.total_rows} rows | 
                  <span className="text-emerald-600 ml-1">Valid: {importDialog.preview.valid_count}</span> | 
                  <span className="text-red-600 ml-1">Invalid: {importDialog.preview.invalid_count}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {importDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2">Processing file...</span>
              </div>
            ) : importDialog.preview ? (
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-2 py-1 border">Row</th>
                    <th className="px-2 py-1 border">Status</th>
                    <th className="px-2 py-1 border">Client Name</th>
                    <th className="px-2 py-1 border">ID</th>
                    <th className="px-2 py-1 border">Exchange</th>
                    <th className="px-2 py-1 border">MCX Bal</th>
                    <th className="px-2 py-1 border">FO Bal</th>
                    <th className="px-2 py-1 border">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {importDialog.preview.rows.map((row, idx) => (
                    <tr key={idx} className={cn(!row.is_valid && "bg-red-50 dark:bg-red-950/20")}>
                      <td className="px-2 py-1 border text-center">{row.row_num}</td>
                      <td className="px-2 py-1 border text-center">
                        {row.is_valid ? (
                          <span className="text-emerald-600">✓</span>
                        ) : (
                          <span className="text-red-600">✗</span>
                        )}
                      </td>
                      <td className="px-2 py-1 border">{row.client_name}</td>
                      <td className="px-2 py-1 border">{row.code_or_id}</td>
                      <td className="px-2 py-1 border">{row.exchange}</td>
                      <td className="px-2 py-1 border text-right">{row.mcx_bal}</td>
                      <td className="px-2 py-1 border text-right">{row.fo_bal}</td>
                      <td className="px-2 py-1 border text-red-600">{row.errors?.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => window.open(weeklyDollarApi.downloadTemplate(), '_blank')}>
              <Download className="h-4 w-4 mr-1" />Download Template
            </Button>
            <Button variant="outline" onClick={() => setImportDialog({ open: false, preview: null, loading: false })}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmImport} 
              disabled={importDialog.loading || !importDialog.preview?.valid_count}
            >
              Import {importDialog.preview?.valid_count || 0} Valid Rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
